import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to user's notifications from Firestore
  useEffect(() => {
    console.log('NotificationContext: Setting up auth state listener');

    // Listen to auth state changes
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        console.log('No authenticated user - clearing notifications');
        setNotifications([]);
        setLoading(false);
        return;
      }

      console.log('User authenticated - setting up notification subscription for:', user.uid);

      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        where('archived', '==', false),
        orderBy('timestamp', 'desc')
      );

      const unsubscribeSnapshot = onSnapshot(
        q,
        (snapshot) => {
          console.log('Notification snapshot received:', {
            size: snapshot.size,
            empty: snapshot.empty
          });

          const notifs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
          }));

          console.log('Loaded notifications:', notifs);
          setNotifications(notifs);
          setLoading(false);
        },
        (error) => {
          console.error('Error loading notifications:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message
          });
          setLoading(false);
        }
      );

      // Return cleanup for snapshot listener
      return unsubscribeSnapshot;
    });

    // Cleanup: unsubscribe from auth state changes
    return () => {
      console.log('Cleaning up notification context');
      unsubscribeAuth();
    };
  }, []);

  // Add notification to Firestore
  const addNotification = useCallback(async (notification) => {
    if (!auth.currentUser) {
      console.warn('Cannot add notification - user not authenticated');
      return;
    }

    try {
      console.log('Adding notification to Firestore:', {
        userId: auth.currentUser.uid,
        notification
      });

      const docRef = await addDoc(collection(db, 'notifications'), {
        userId: auth.currentUser.uid,
        timestamp: serverTimestamp(),
        read: false,
        archived: false,
        ...notification,
      });

      console.log('Notification added successfully with ID:', docRef.id);
    } catch (error) {
      console.error('Error adding notification:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
  }, []);

  // Mark notification as read in Firestore
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const promises = notifications
        .filter(n => !n.read)
        .map(n => updateDoc(doc(db, 'notifications', n.id), { read: true }));
      await Promise.all(promises);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [notifications]);

  // Archive a notification (soft delete)
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        archived: true
      });
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  }, []);

  // Clear all notifications (archive all)
  const clearAllNotifications = useCallback(async () => {
    try {
      const promises = notifications.map(n =>
        updateDoc(doc(db, 'notifications', n.id), { archived: true })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, [notifications]);

  // Show toast notification (temporary popup - still in memory)
  const showToast = useCallback((toast) => {
    const newToast = {
      id: Date.now() + Math.random(),
      duration: 4000,
      ...toast,
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  // Remove toast manually
  const removeToast = useCallback((toastId) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  // Combined function: show toast AND add to notification center
  const notify = useCallback(
    ({ message, type = 'info', title, category, showInCenter = true, duration = 4000 }) => {
      // Auto-detect category if not provided based on business requirements
      let autoCategory = category;
      if (!autoCategory) {
        // RULE 1: Check for explicit quiz actions (quiz management)
        if (title?.toLowerCase().includes('quiz') || message?.toLowerCase().includes('quiz')) {
          if (type === 'success') {
            // "Quiz Created", "Quiz Updated", "Quiz Published" → quiz category
            if (
              title?.toLowerCase().includes('created') ||
              title?.toLowerCase().includes('updated') ||
              title?.toLowerCase().includes('published') ||
              title?.toLowerCase().includes('deleted') ||
              title?.toLowerCase().includes('shared')
            ) {
              autoCategory = 'quiz';
            } else {
              // Other quiz-related success (like quiz submission) → system
              autoCategory = 'system';
            }
          } else if (type === 'error') {
            // "Failed to create quiz", "Failed to submit quiz" → system
            autoCategory = 'system';
          } else {
            autoCategory = 'system';
          }
        }
        // RULE 2: Check for achievement keywords
        else if (
          title?.toLowerCase().includes('achievement') ||
          title?.toLowerCase().includes('streak') ||
          title?.toLowerCase().includes('milestone') ||
          title?.toLowerCase().includes('unlocked') ||
          title?.toLowerCase().includes('perfect score') ||
          title?.toLowerCase().includes('congratulations') ||
          message?.toLowerCase().includes('congratulations') ||
          type === 'achievement'
        ) {
          autoCategory = 'achievement';
        }
        // RULE 3: Errors and warnings always go to system
        else if (type === 'error' || type === 'warning' || type === 'info') {
          autoCategory = 'system';
        }
        // RULE 4: Default fallback
        else {
          autoCategory = type === 'success' ? 'quiz' : 'system';
        }
      }

      // Show toast (temporary)
      showToast({ message, type, title, duration });

      // Add to notification center (persisted)
      if (showInCenter) {
        addNotification({ message, type, title, category: autoCategory });
      }
    },
    [showToast, addNotification]
  );

  const unreadCount = notifications.filter((n) => !n.read && !n.archived).length;

  const value = {
    notifications,
    toasts,
    unreadCount,
    loading,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    showToast,
    removeToast,
    notify,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
